import React from "react";
import { Tooltip } from "@/components/common/Tooltip";

interface IntentBadgesProps {
  isNullable?: boolean;
  isDistinct?: boolean;
  isPrimaryKeyMember?: boolean;
}

export type IntentVariant = "info" | "default" | "warning";

const COLOR: Record<IntentVariant, string> = {
  info: "var(--color-accent)",
  default: "var(--color-fg-muted)",
  warning: "var(--color-warning)",
};

/** One single-glyph intent chip: a fixed square so the letter is centered with
 *  consistent padding, transparent background, colored border == colored text.
 *  `violated` re-tones it to error and strikes the glyph through — the same
 *  badge, shown as "this intent isn't holding". */
export const IntentChip: React.FC<{
  letter: string;
  variant: IntentVariant;
  label: string;
  tooltip: string;
  violated?: boolean;
}> = ({ letter, variant, label, tooltip, violated }) => {
  const color = violated ? "var(--color-error)" : COLOR[variant];
  return (
    <Tooltip title={tooltip}>
      <span
        aria-label={label}
        style={{
          display: "inline-flex",
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
          width: "0.875rem",
          height: "0.875rem",
          fontSize: "var(--font-size-x-small)",
          fontWeight: "var(--font-weight-semibold)",
          lineHeight: 1,
          color,
          background: "transparent",
          border: `1px solid ${color}`,
          borderRadius: "var(--radius-app)",
          textDecoration: violated ? "line-through" : undefined,
        }}
      >
        {letter}
      </span>
    </Tooltip>
  );
};

/**
 * Compact single-letter corner-chips for a column's semantic intents (hints,
 * not enforced constraints). They live on the column's name row, so they stay
 * one glyph wide — K / U / R — to keep the name legible; the tooltip and
 * aria-label carry the full meaning. Rendered only for intents that are set,
 * in a stable order:
 *
 *   K — KEY      primary-key member  (info / accent)
 *   U — UNIQUE   distinct            (default / neutral)
 *   R — REQUIRED not nullable        (warning)
 *
 * Tooltip copy stays intent-framed ("should", not "enforced").
 */
export const IntentBadges: React.FC<IntentBadgesProps> = ({
  isNullable,
  isDistinct,
  isPrimaryKeyMember,
}) => {
  const required = isNullable === false;
  if (!isPrimaryKeyMember && !isDistinct && !required) return null;

  return (
    <>
      {isPrimaryKeyMember && (
        <IntentChip
          letter="K"
          variant="info"
          label="Key"
          tooltip="Key — part of the primary key"
        />
      )}
      {isDistinct && (
        <IntentChip
          letter="U"
          variant="default"
          label="Unique"
          tooltip="Unique — values should be distinct"
        />
      )}
      {required && (
        <IntentChip
          letter="R"
          variant="warning"
          label="Required"
          tooltip="Required — values should not be empty"
        />
      )}
    </>
  );
};
